import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { useUnmount } from 'react-use';

import CloudflareResizedImage from 'components/CloudflareResizedImage';
import Modal from 'components/Modal';
import { PlayIcon } from 'components/Icons';
import { renderJWElements, unmountJWElements } from 'utils/renderCustomComponents';

import Styles from './TravelInterview.module.scss';

const VideoPlayer: React.FC<{ displayAds?: boolean; videoId: string; playerId?: string }> = ({
  videoId,
  playerId,
  displayAds = false,
}) => {
  useEffect(() => {
    renderJWElements();
  }, []);

  let currentPlayerID: string = 'VCOtwWVi'; // NO ADs

  if (playerId) {
    currentPlayerID = playerId;
  } else if (displayAds) {
    currentPlayerID = 'zPGq5GVv';
  }

  useUnmount(() => {
    unmountJWElements();
  });

  return (
    <div className={Styles.videoWrapper} data-testid="travel-interview-video-player">
      <div id={`jwplayer_${videoId}_${currentPlayerID}_div`} />
    </div>
  );
};

const RICHTEXT_OPTIONS = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_: any, children: any) => (
      <Typography className={Styles.paragraph} component="p" variant="title1">
        {children}
      </Typography>
    ),
  },
};

export interface TravelInterviewProps {
  attribution: string;
  colorPalette: {
    primary: string;
    secondary: string;
    accent: string;
  };
  displayAds?: boolean;
  excerpt: any;
  imageUrl: string;
  playerId?: string;
  videoCta?: string;
  videoId?: string;
  videoPosterUrl?: string;
}

export const TravelInterview: React.FC<TravelInterviewProps> = ({
  attribution,
  colorPalette,
  displayAds,
  excerpt,
  imageUrl,
  playerId,
  videoCta = 'Watch Interview',
  videoId,
  videoPosterUrl,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  return (
    <section
      className={Styles.travelInterview}
      style={{
        // @ts-ignore
        '--color-palette-primary': colorPalette.primary,
        '--color-palette-secondary': colorPalette.secondary,
        '--color-palette-accent': colorPalette.accent,
      }}
      data-testid="travel-interview"
    >
      <div className={Styles.image}>
        <CloudflareResizedImage
          src={imageUrl || videoPosterUrl!}
          layout="fill"
          alt=""
          loading="lazy"
          objectFit="cover"
          objectPosition="center"
          sizes="(min-width: 769px) 50vw, (max-width: 768px) 100vw"
        />
      </div>
      <div className={Styles.content}>
        <figure className={Styles.figure}>
          <Typography className={Styles.blockquote} component="blockquote" variant="title1">
            {documentToReactComponents(excerpt, RICHTEXT_OPTIONS)}
          </Typography>
          <Typography className={Styles.figcaption} component="figcaption" variant="title3">
            - {attribution}
          </Typography>
        </figure>
        {videoId && (
          <>
            <button
              type="button"
              className={Styles.watchButton}
              onClick={openModal}
              data-testid="travel-interview-video-trigger"
            >
              <div className={Styles.cta}>
                <PlayIcon className={Styles.watchIcon} />
                <Typography className={Styles.watchText} component="span" variant="title3">
                  {videoCta}
                </Typography>
              </div>
              <div className={Styles.watchImage}>
                <CloudflareResizedImage
                  src={videoPosterUrl!}
                  layout="fixed"
                  width={296}
                  height={166.5}
                  alt=""
                  loading="lazy"
                  objectFit="cover"
                  objectPosition="center"
                />
              </div>
            </button>
            <Modal open={isModalOpen} setOpen={setIsModalOpen}>
              <VideoPlayer displayAds={displayAds} playerId={playerId} videoId={videoId} />
            </Modal>
          </>
        )}
        {!videoId && videoPosterUrl && (
          <div className={Styles.watchStatic}>
            <div className={Styles.watchImage}>
              <CloudflareResizedImage
                src={videoPosterUrl}
                layout="fixed"
                width={296}
                height={166.5}
                alt=""
                loading="lazy"
                objectFit="cover"
                objectPosition="center"
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TravelInterview;
